import React from 'react';
import { useAdmin } from '../../contexts/AdminContext';

/**
 * PermissionGuard component - protects content based on user roles
 * @param {string} requiredRole - minimum role required ('viewer', 'editor', 'admin')
 * @param {React.ReactNode} children - content to render if user has permission
 * @param {React.ReactNode} fallback - content to render if user lacks permission
 * @param {boolean} exact - if true, requires exact role match instead of minimum level
 */
function PermissionGuard({ 
  requiredRole, 
  children, 
  fallback = null, 
  exact = false 
}) {
  const { user, hasRole, loading } = useAdmin();

  // Show loading state
  if (loading) {
    return (
      <div className="permission-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // No user logged in
  if (!user) {
    return fallback || (
      <div className="permission-denied">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Authentication required to access this content.
        </div>
      </div>
    );
  }

  // Check permission
  const hasPermission = exact 
    ? user.role === requiredRole 
    : hasRole(requiredRole);

  if (!hasPermission) {
    return fallback || (
      <div className="permission-denied">
        <div className="alert alert-danger">
          <i className="fas fa-lock me-2"></i>
          Access denied. {exact ? `${requiredRole} role` : `Minimum ${requiredRole} role`} required.
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * AdminOnly component - shorthand for admin-only content
 */
export function AdminOnly({ children, fallback }) {
  return (
    <PermissionGuard requiredRole="admin" fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * EditorAndUp component - shorthand for editor and admin content
 */
export function EditorAndUp({ children, fallback }) {
  return (
    <PermissionGuard requiredRole="editor" fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * ViewerAndUp component - shorthand for any authenticated user
 */
export function ViewerAndUp({ children, fallback }) {
  return (
    <PermissionGuard requiredRole="viewer" fallback={fallback}>
      {children}
    </PermissionGuard>
  );
}

/**
 * RoleIndicator component - shows user's current role
 */
export function RoleIndicator({ showIcon = true, showLabel = true, className = '' }) {
  const { user } = useAdmin();

  if (!user) return null;

  const roleConfig = {
    admin: {
      color: 'danger',
      icon: 'fas fa-user-shield',
      label: 'Administrator'
    },
    editor: {
      color: 'primary', 
      icon: 'fas fa-user-edit',
      label: 'Editor'
    },
    viewer: {
      color: 'secondary',
      icon: 'fas fa-user-check', 
      label: 'Viewer'
    }
  };

  const config = roleConfig[user.role] || roleConfig.viewer;

  return (
    <span className={`badge bg-${config.color} ${className}`}>
      {showIcon && <i className={`${config.icon} me-1`}></i>}
      {showLabel && config.label}
    </span>
  );
}

/**
 * PermissionButton component - button that's only visible/enabled based on permissions
 */
export function PermissionButton({ 
  requiredRole, 
  exact = false, 
  children, 
  className = '', 
  disabled = false,
  ...props 
}) {
  const { user, hasRole } = useAdmin();

  if (!user) return null;

  const hasPermission = exact 
    ? user.role === requiredRole 
    : hasRole(requiredRole);

  if (!hasPermission) return null;

  return (
    <button 
      className={className}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * ConditionalRender component - renders different content based on role
 */
export function ConditionalRender({ 
  admin, 
  editor, 
  viewer, 
  fallback = null 
}) {
  const { user } = useAdmin();

  if (!user) return fallback;

  switch (user.role) {
    case 'admin':
      return admin || fallback;
    case 'editor': 
      return editor || fallback;
    case 'viewer':
      return viewer || fallback;
    default:
      return fallback;
  }
}

export default PermissionGuard;